import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'

import { makeStyles } from 'tss-react/mui'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ReorderIcon from '@mui/icons-material/Reorder'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import {
    Card,
    CardContent,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { ResultadoAnalise } from 'src/modules/bordero/bordero.types'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import DialogoResultadoScope from '../dialogo-resultado.scopes'

const LOG = Logger.get('DIALOGO-RESULTADO-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type DialogoResultadoViewProps = {
    className?: string
    scope: DialogoResultadoScope
}
class DialogoResultadoViewClass implements FCClassContext<DialogoResultadoViewProps> {
    // :: Fields
    scope!: DialogoResultadoScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors
    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: DialogoResultadoViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: DialogoResultadoViewProps) {
        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10, minWidth: 700, height: 600 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="body1">{scope.origem}</Typography>
                            <Divider />
                            <Typography mt={2} ml={1} variant="h6">
                                {scope.nome}
                            </Typography>
                            <Typography ml={1} variant="body2">
                                {scope.documento}
                            </Typography>
                        </CardContent>
                    </Card>
                    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
                        <Table size="small" aria-label="collapsible table">
                            <TableBody>
                                <CategoryRow key="BLOQUEAR" category="BLOQUEAR" rows={scope.resultado?.bloqueio} />
                                <CategoryRow key="ALERTAR" category="ALERTAR" rows={scope.resultado?.alerta} />
                                <CategoryRow key="LIBERAR" category="LIBERAR" rows={scope.resultado?.liberar} />
                                <CategoryRow key="SUCESSO" category="SUCESSO" rows={scope.resultado?.sucesso} />
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Divider />

                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px" marginTop="10px">
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Fechar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const DialogoResultadoView = classToFComponent(DialogoResultadoViewClass, React)
export default DialogoResultadoView

function CategoryRow(props: { category: string; rows: ResultadoAnalise[] }) {
    const { category, rows } = props
    const [open, setOpen] = React.useState(false)

    if (!rows) {
        return null
    }

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell width={30}>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell
                    component="th"
                    scope="row"
                    sx={
                        category === 'BLOQUEAR'
                            ? {
                                  color: (theme) => theme.palette.error.dark,
                                  fontWeight: 'bold'
                              }
                            : category === 'ALERTAR'
                            ? {
                                  color: (theme) => theme.palette.warning.dark,
                                  fontWeight: 'bold'
                              }
                            : category === 'LIBERAR'
                            ? {
                                  color: (theme) => theme.palette.info.dark,
                                  fontWeight: 'bold'
                              }
                            : category === 'SUCESSO'
                            ? {
                                  color: (theme) => theme.palette.success.dark,
                                  fontWeight: 'bold'
                              }
                            : {}
                    }
                >
                    {category === 'BLOQUEAR' ? (
                        <>
                            <ErrorOutlineIcon color="error" fontSize="small" sx={{ verticalAlign: 'top' }} />{' '}
                            {`Bloqueios (${rows.length})`}
                        </>
                    ) : category === 'ALERTAR' ? (
                        <>
                            <WarningAmberIcon color="warning" fontSize="small" sx={{ verticalAlign: 'top' }} />{' '}
                            {`Alertas (${rows.length})`}
                        </>
                    ) : category === 'LIBERAR' ? (
                        <>
                            <CheckCircleIcon color="info" fontSize="small" sx={{ verticalAlign: 'top' }} />{' '}
                            {`Liberados (${rows.length})`}
                        </>
                    ) : category === 'SUCESSO' ? (
                        <>
                            <CheckCircleIcon color="success" fontSize="small" sx={{ verticalAlign: 'top' }} />{' '}
                            {`Verificados sem problemas (${rows.length})`}
                        </>
                    ) : (
                        ''
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small" aria-label="collapsible table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Análise</TableCell>
                                        <TableCell>Regra</TableCell>
                                        <TableCell align="center">Rating 1</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((resultRow) => (
                                        <TableRow key={resultRow.id}>
                                            <TableCell>{resultRow.analiseNome}</TableCell>
                                            <TableCell>
                                                <ReorderIcon fontSize="small" /> {resultRow.regraNome}
                                            </TableCell>
                                            <TableCell align="center">
                                                {category !== 'SUCESSO' ? '0' : resultRow.rating1}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    )
}
